import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Input, Tabs } from '@fikasio/react-ui-components';
import { useTranslation } from 'react-i18next';
import PaneHeader from 'components/UI/PaneHeader';
import { clearPaneContent } from 'services/pane/slice';
import { useGetTaskQuery, useAddTaskMutation, usePatchTaskMutation } from 'services/tasks/api';
import { Task } from 'services/tasks/types';
import links, { linkKey } from 'utils/links';
import { toJSDate } from 'utils/date';
import TaskDiscussion from './TaskDiscussion';
import TaskInformationsForm from './TaskInformationsForm';
import TaskSubtasks from './TaskSubtasks';
import TaskTimeEntries from './TaskTimeEntries';
import styles from './index.module.css';

interface TaskPaneProps {
  assignee?: string;
  dueAt?: Date | string;
  id: string;
  projectId?: string;
  showExpandIcon?: boolean;
}

const TaskPane = ({
  assignee,
  dueAt,
  id,
  projectId,
  showExpandIcon = true,
}: TaskPaneProps) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { data: taskFromQuery } = useGetTaskQuery(id, { skip: !id || id === 'NEW' });
  const [createdTask, setCreatedTask] = useState<{ paneId: string; task: Task }>();
  const task = taskFromQuery || (createdTask?.paneId === id ? createdTask.task : undefined);
  const expandId = task?.id && task.id !== 'NEW' ? linkKey(task) : undefined;
  const defaultDueAt = toJSDate(dueAt);
  const taskDefaults: Partial<Task> = {
    ...(assignee ? { assignee } : {}),
    ...(defaultDueAt ? { dueAt: defaultDueAt } : {}),
    ...(projectId ? { projects: [{ id: projectId }] } : {}),
  };

  const [createTask] = useAddTaskMutation();
  const [patchTask] = usePatchTaskMutation();
  const descriptionRef = useRef(task?.description ?? '');
  const descriptionTouchedRef = useRef(false);
  const [tab, setTab] = useState('INFOS');

  useEffect(() => {
    descriptionTouchedRef.current = false;
    setTab('INFOS');
  }, [id]);

  useEffect(() => {
    if (descriptionTouchedRef.current) {
      return;
    }
    descriptionRef.current = task?.description ?? '';
  }, [id, task?.description]);

  return (
    <TaskInformationsForm
      className={styles.form}
      descriptionRef={descriptionRef}
      onClose={() => dispatch(clearPaneContent())}
      projectId={projectId}
      task={task}
      taskDefaults={taskDefaults}
    >
      {({ actions, details, metadata }) => (
        <div className={styles.layout} data-tab={tab}>
          <div className={styles.name}>
            <PaneHeader
              expandTo={expandId ? links.task(expandId) : undefined}
              showExpandIcon={showExpandIcon}
            >
              <Input
                key={id}
                defaultValue={task?.description ?? ''}
                onChange={description => {
                  descriptionTouchedRef.current = true;
                  descriptionRef.current = description;
                }}
                onSave={description => {
                  if (task && task.id) {
                    patchTask({
                      id: task.id,
                      description,
                    });
                  } else if (description) {
                    createTask({
                      description,
                      ...taskDefaults,
                    })
                      .then(({ data }) => {
                        if (data) {
                          setCreatedTask({ paneId: id, task: data });
                        }
                      });
                  }
                }}
                style={{
                  fontSize: 24,
                  fontWeight: 'bold',
                  height: 40,
                  marginBottom: 0,
                  padding: 0,
                  width: '100%',
                }}
              />
            </PaneHeader>
          </div>
          {
            task?.id && (
              <div className={styles.tabBar}>
                <Tabs
                  onChange={setTab}
                  options={[
                    { label: t('informations'), value: 'INFOS' },
                    { label: t('time'), value: 'TIME' },
                  ]}
                  value={tab}
                />
              </div>
            )
          }
          <div className={styles.primary}>
              <div className={styles.details}>
                {details}
              </div>
              {
                task?.id && task.type !== 'Instance' && (
                  <div className={styles.subtasks}>
                    <TaskSubtasks parentTask={task} />
                  </div>
                )
              }
              {
                task?.id && (
                  <div className={styles.discussion}>
                    <div className={styles.sectionHeading}>{t('discussion')}</div>
                    <TaskDiscussion task={task} />
                  </div>
                )
              }
          </div>
          {
            task?.id && (
              <div className={styles.timeEntries}>
                <TaskTimeEntries task={task} />
              </div>
            )
          }
          <div className={styles.meta}>
            {metadata}
          </div>
          <div className={styles.actions}>
            {actions}
          </div>
        </div>
      )}
    </TaskInformationsForm>
  );
};

export default TaskPane;
